import { P2GBox, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { lazy, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import useLocalStorage from '~/hooks/use-local-storage';
import RequireCustomer from '../layouts/my-account/require-customer';
import { ContentContext } from '@parcel2go/npm-p2g-app';
import { RoutingConstants, RoutingConstantsTabs } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { LocalStorageKeys } from '~/apps/smartsend/lib/consts/LocalStorageKeys';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '../use-on-load';
import DesktopTitledLayout from '../layouts/my-account/desktop-layout';
import MobileLayout from '../layouts/my-account/mobile-layout';
import { GetDefaultSubItem, SubNavItemType, SubNavTabs } from '../groups/sub-nav';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { EntitlementKeys, useHasEntitlement } from '~/hooks/use-entitlements';

const OverviewDash = lazy(() => import('./overview'));
const InsightsDashboard = lazy(() => import('./insights'));
const CostDash = lazy(() => import('./costs'));
const PerformanceDash = lazy(() => import('./performance'));
const InternationalDash = lazy(() => import('./international'));

const Wrapper = ({ isXs, children }: { isXs: boolean; children: React.ReactNode }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return isXs ? (
    <MobileLayout>{children}</MobileLayout>
  ) : (
    <DesktopTitledLayout title={t('SMARTSEND_ANALYTICS_DASH_TITLE', { defaultValue: 'Analytics' })}>
      {children}
    </DesktopTitledLayout>
  );
};

export default function Component() {
  const { track } = useSmartSendAnalytics();
  const isXs = useP2GMediaQueryOnly('xs');
  const serviceAnalyticsEnabled = useHasEntitlement(EntitlementKeys.SERVICE_ANALYTICS);

  const options: SubNavItemType[] = useMemo(() => {
    const output = isXs
      ? [
          {
            translationKey: 'TAB_ANALYTICS_VIEW_OVERVIEW_LABEL',
            label: 'Overview',
            tab: RoutingConstantsTabs.OverviewDashboard
          },
          {
            translationKey: 'TAB_ANALYTICS_VIEW_RECOMMENDATIONS_LABEL',
            label: 'Usage',
            tab: RoutingConstantsTabs.InsightsDashboard
          }
        ]
      : [
          {
            translationKey: 'TAB_ANALYTICS_VIEW_OVERVIEW_LABEL',
            label: 'Overview',
            tab: RoutingConstantsTabs.OverviewDashboard
          },
          {
            translationKey: 'TAB_ANALYTICS_VIEW_INSIGHTS_LABEL',
            label: 'Insights',
            tab: RoutingConstantsTabs.InsightsDashboard
          },
          {
            translationKey: 'TAB_ANALYTICS_VIEW_PERFORMANCE_LABEL',
            label: 'Courier Performance',
            tab: RoutingConstantsTabs.PerformanceDashboard
          },
          {
            translationKey: 'TAB_ANALYTICS_VIEW_INTERNATIONAL_LABEL',
            label: 'International',
            tab: RoutingConstantsTabs.InternationalDashboard
          },
          {
            translationKey: 'TAB_ANALYTICS_VIEW_COST_LABEL',
            label: 'Costs',
            tab: RoutingConstantsTabs.CostDashboard
          }
        ];

    return output.filter((s) => {
      switch (s.tab) {
        case RoutingConstantsTabs.OverviewDashboard:
          return true;

        case RoutingConstantsTabs.InsightsDashboard:
          return SettingsCache.settings.Features.includes('Dashboard:Insights');

        case RoutingConstantsTabs.InternationalDashboard:
          return serviceAnalyticsEnabled;

        case RoutingConstantsTabs.CostDashboard:
          return SettingsCache.settings.Features.includes('Dashboard:Costs');

        case RoutingConstantsTabs.PerformanceDashboard:
          return serviceAnalyticsEnabled || SettingsCache.settings.Features.includes('Dashboard:Performance');

        default:
          return false;
      }
    });
  }, [isXs, serviceAnalyticsEnabled]);

  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { setValue: setIsSmartSendUser } = useLocalStorage(LocalStorageKeys.HasVisitedSmartSend, true);
  const [selectedOption, setSelectedOption] = useState<string>(GetDefaultSubItem(options));

  useEffect(() => {
    if (selectedOption) {
      window.location.hash = selectedOption;

      track({
        type: 'page_view',
        payload: {
          ...GenerateBaseAnalyticsPayload([]),
          page: selectedOption,
          area: RoutingConstants.Analytics
        }
      });
    }
  }, [selectedOption, track]);

  useOnLoad(() => {
    const title = t('SMARTSEND_ANALYTICS_PAGE_TITLE', { defaultValue: 'Analytics' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.SMARTSEND });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: {
        title: title,
        isHome: true,
        currentPageRoute: RoutingConstants.Analytics
      }
    });
    setIsSmartSendUser(true);
  });

  return (
    <Suspense>
      <Wrapper isXs={isXs}>
        <RequireCustomer isXs={isXs} silent={true}>
          <SubNavTabs
            options={options}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            translationNameSpace={TranslationNamespaces.MYACCOUNT}
          />
          {selectedOption === RoutingConstantsTabs.OverviewDashboard && (
            <Suspense>
              <P2GBox>
                <OverviewDash isXs={isXs} />
              </P2GBox>
            </Suspense>
          )}
          {selectedOption === RoutingConstantsTabs.InsightsDashboard && (
            <Suspense>
              <P2GBox>
                <InsightsDashboard isXs={isXs} />
              </P2GBox>
            </Suspense>
          )}
          {selectedOption === RoutingConstantsTabs.PerformanceDashboard && (
            <Suspense>
              <P2GBox>
                <PerformanceDash isXs={isXs} />
              </P2GBox>
            </Suspense>
          )}
          {selectedOption === RoutingConstantsTabs.InternationalDashboard && (
            <Suspense>
              <P2GBox>
                <InternationalDash isXs={isXs} />
              </P2GBox>
            </Suspense>
          )}
          {selectedOption === RoutingConstantsTabs.CostDashboard && (
            <Suspense>
              <P2GBox>
                <CostDash isXs={isXs} />
              </P2GBox>
            </Suspense>
          )}
        </RequireCustomer>
      </Wrapper>
    </Suspense>
  );
}

Component.displayName = 'SmartSend';
